import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Symbols,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { defaultSeriesColor, defaultSeriesShape, formatDateAxisValue, isNumericXAxis, seriesDisplayLabel, toRechartsRows, type MarkerShape, niceAxisScale } from './dataviz-utils';

import type { ResolvedDataVizData } from '@agavi/blocks';

interface DataVizCanvasProps {
  data: ResolvedDataVizData;
  height?: number;
  /** The point currently sounding during sonification playback — sighted/low-vision users following
   * along get a visual anchor for "this is the tone you're hearing right now", not just audio. */
  highlightedPoint?: { x: number | string; y: number } | null;
  /** The series (if any) two-click-armed for removal in the grid editor — recolored to the warning
   * color and the others dimmed, matching how every other arm-to-delete control in the app
   * highlights the whole thing about to be removed, not just its own trash icon. */
  armedSeriesId?: string | null;
  /** The x-position implied by elapsed sonification playback time, updated every animation frame —
   * renders as a moving vertical line so the chart visibly keeps progressing through silent
   * stretches (before the first point, between disjoint clusters, after the last one) instead of
   * looking frozen just because no tone happens to be sounding at that instant. */
  playheadX?: number | null;
}

// Recharts renders unstyled inline SVG by default — these theme the chart chrome (grid, axis
// lines, tick labels, tooltip, legend) against the app's own design tokens instead of leaving
// Recharts' hardcoded library defaults, matching how every other block's chrome is themed.
const GRID_STROKE = 'var(--color-ws-border)';
const AXIS_STROKE = 'var(--color-ws-border)';
const TICK_STYLE = { fill: 'var(--color-ws-muted)', fontSize: 12 };
const AXIS_LABEL_STYLE = { fill: 'var(--color-ws-muted)', fontSize: 12, fontWeight: 500 };
// Recharts doesn't auto-center a rotated label around its own pivot — without an explicit
// `textAnchor: 'middle'`, a -90deg-rotated y-axis label anchors from its start point instead of
// its center, which reads as "too low" (or, with a different `position`, clipped off the top).
const Y_AXIS_LABEL_STYLE = { ...AXIS_LABEL_STYLE, textAnchor: 'middle' as const };
const TOOLTIP_CONTENT_STYLE: React.CSSProperties = {
  background: 'var(--color-ws-surface)',
  border: '1px solid var(--color-ws-border)',
  borderRadius: 8,
  fontSize: 13,
  color: 'var(--color-ws-text)',
};

// Distinct shapes per series so overlapping marks are distinguishable beyond color alone — reuses
// Recharts' own `Symbols` (the same d3-shape-backed renderer its `legendType` prop draws in the
// legend), rather than hand-authoring per-shape path/polygon math. `sizeType='diameter'` keeps a
// consistent apparent size across the shape family, so e.g. a triangle at diameter 6 doesn't read
// heavier or lighter than a circle at the same diameter.
interface SeriesMarkerProps {
  cx?: number;
  cy?: number;
  shape: MarkerShape;
  size: number;
  fill: string;
  dimmed?: boolean;
}

function SeriesMarker({ cx, cy, shape, size, fill, dimmed }: SeriesMarkerProps) {
  if (cx === undefined || cy === undefined) return null;
  return (
    <Symbols
      cx={cx}
      cy={cy}
      type={shape}
      size={size}
      sizeType='diameter'
      fill={fill}
      opacity={dimmed ? 0.3 : 1}
    />
  );
}

interface DataVizLegendProps {
  data: ResolvedDataVizData;
  hoveredSeriesId: string | null;
  onHoverSeries: (id: string | null) => void;
  armedSeriesId?: string | null;
  seriesColor: (index: number, s: ResolvedDataVizData['series'][number]) => string;
  seriesName: (s: ResolvedDataVizData['series'][number], index: number) => string;
}

// A plain HTML row rendered as a normal sibling BELOW the chart's <ResponsiveContainer>, instead of
// Recharts' own built-in <Legend> — that component positions itself at fixed offsets from the axis
// line that don't reconcile with an x-axis label's own (also fixed) position, no matter how the
// surrounding margin/height are tuned (confirmed empirically: the gap between the two never budges).
// A plain flex row after the chart sizes itself from ordinary content + a CSS gap, so the two can
// never collide and the spacing is exactly whatever gap-* says, nothing more.
function DataVizLegend({ data, hoveredSeriesId, onHoverSeries, armedSeriesId, seriesColor, seriesName }: DataVizLegendProps) {
  return (
    <div className='flex flex-wrap items-center justify-center gap-x-4 gap-y-1'>
      {data.series.map((s, i) => {
        const dimmed = (hoveredSeriesId !== null && hoveredSeriesId !== s.id) || (!!armedSeriesId && armedSeriesId !== s.id);
        return (
          <button
            key={s.id}
            type='button'
            onMouseEnter={() => onHoverSeries(s.id)}
            onMouseLeave={() => onHoverSeries(null)}
            onFocus={() => onHoverSeries(s.id)}
            onBlur={() => onHoverSeries(null)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            className='flex items-center gap-1.5 text-sm text-ws-muted'
            style={{ opacity: dimmed ? 0.4 : 1 }}
          >
            <svg width={12} height={12} viewBox='0 0 12 12' aria-hidden='true' className='shrink-0'>
              <Symbols cx={6} cy={6} type={defaultSeriesShape(i)} size={8} sizeType='diameter' fill={seriesColor(i, s)} />
            </svg>
            {seriesName(s, i)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Memoised, and this is the single most load-bearing `memo` in the editor.
 *
 * A chart with a few hundred points is thousands of React elements and SVG nodes, and it was being
 * rebuilt on EVERY render of the task canvas — including every keystroke into an unrelated block's
 * feedback box, because `renderBlock` hands every block a freshly-closed-over `sharedProps`. A CPU
 * profile of typing 25 characters into a Slider's feedback put recharts at the top: `ScatterPoint`
 * alone was 11.5% of the samples, and the chart's share of the work was over a third.
 *
 * Every prop here is either a scalar or the block's own `data`, which keeps its identity when a
 * DIFFERENT block is edited — so this memo actually hits, unlike one placed on the block component
 * itself. That is the whole reason the boundary is drawn at the canvas and not higher up: the
 * enclosing block is handed new callbacks on every render and can never be memoised as it stands.
 *
 * `memo` does not block context, so a theme or locale change still repaints the chart.
 */
export const DataVizCanvas = memo(function DataVizCanvas({ data, height = 320, highlightedPoint, armedSeriesId, playheadX }: DataVizCanvasProps) {
  const { t } = useTranslation();
  // Tracks which legend entry (if any) the pointer is over, so its series can be visually
  // emphasized and the rest dimmed — useful once overlapping marks/lines make color-matching hard.
  const [hoveredSeriesId, setHoveredSeriesId] = useState<string | null>(null);
  const hasData = data.series.some((s) => s.points.length > 0);
  if (!hasData) return null;

  const numericX = isNumericXAxis(data.series);
  const showLegend = data.series.length > 1;
  // Recharts' `accessibilityLayer` (on by default) gives the chart's root SVG a keyboard-navigable
  // `role='application'` with no accessible name of its own — without this, Tab lands on a silent,
  // unlabeled "application" region. Recharts renders this straight into an SVG <title>, which is
  // what actually supplies that name.
  const chartAccessibleTitle = data.title || t('blocks.dataviz.chartRegionLabel');
  const seriesColor = (index: number, s: (typeof data.series)[number]) => (armedSeriesId === s.id ? 'var(--color-ws-warning-fg)' : s.color ?? defaultSeriesColor(index));
  const seriesName = (s: (typeof data.series)[number], index: number) => seriesDisplayLabel(s, index, data.series.length, data.yAxisLabel, t);
  // Dims every OTHER series, whether the current highlight is a legend hover or an armed-for-removal
  // series — same visual mechanism, different trigger.
  const isDimmed = (id: string) => (hoveredSeriesId !== null && hoveredSeriesId !== id) || (!!armedSeriesId && armedSeriesId !== id);
  // margin.bottom just needs to be tall enough that the x-axis label doesn't get clipped by the
  // plot's own bottom edge — the legend is a plain HTML row below the chart now (see DataVizLegend),
  // not Recharts' own <Legend>, so it no longer has any bearing on this margin at all.
  const margin = { top: data.title ? 4 : 8, right: 16, bottom: data.xAxisLabel ? 20 : 8, left: data.yAxisLabel ? 12 : 8 };
  // Recharts' <YAxis> defaults to a fixed 60px width regardless of the actual tick content — too
  // wide for small numbers (the "too much whitespace" report) but, just as importantly, NOT WIDE
  // ENOUGH for large ones: verified live that even the 60px default lets a 6-digit tick (e.g.
  // "140000") overlap the rotated y-axis label, since Recharts' ticks are plain numbers with no
  // comma formatting. The label's own position is a fixed offset (governed by margin.left, not by
  // this width), so the width just needs to reserve enough room for the widest tick text plus a
  // safety gap before that fixed point — sized from the data's own magnitude instead of guessing a
  // single constant that's simultaneously too wide for some charts and too narrow for others.
  const yValues = data.series.flatMap((s) => s.points.map((p) => p.y)).filter((v) => Number.isFinite(v));
  // The y domain and its ticks are OURS, not Recharts' undocumented guess — see `niceAxisScale`.
  // The sonification player states this range aloud, and it can only do that honestly if the
  // number it says and the number drawn here come from the same call.
  const { domain: yDomain, ticks: yTicks } = niceAxisScale(
    yValues,
    data.sonificationThresholds?.y,
    // Bars are read by LENGTH, so a truncated axis misstates the data rather than merely cropping
    // it. Line and scatter are read by position and are free to zoom in.
    data.chartType === 'bar' ? 0 : undefined,
  );
  // Sized from the widest tick actually RENDERED, not from the data's magnitude. The old heuristic
  // measured the integer part of the largest value, which under a 0.5 step reserves room for "2"
  // and then draws "-0.5" — and the rotated axis label sits at a fixed offset, so the shortfall
  // shows up as the label crowding the numbers rather than as a clipped tick.
  const widestYTick = Math.max(1, ...yTicks.map((v) => String(v).length));
  const yAxisWidth = Math.round(data.yAxisLabel ? Math.max(40, widestYTick * 7.5 + 30) : Math.max(28, widestYTick * 7.5 + 14));
  const yAxisLabelProps = data.yAxisLabel ? { value: data.yAxisLabel, angle: -90, position: 'insideLeft' as const, style: Y_AXIS_LABEL_STYLE } : undefined;
  const xAxisLabelProps = data.xAxisLabel ? { value: data.xAxisLabel, position: 'bottom' as const, style: AXIS_LABEL_STYLE, offset: 0 } : undefined;
  // x values are epoch-ms when xAxisType is 'date' (set by CSV import) — format ticks/tooltip as
  // readable dates instead of raw millisecond numbers.
  const xTickFormatter = data.xAxisType === 'date' ? (v: number) => formatDateAxisValue(v) : undefined;
  const xTooltipLabelFormatter = data.xAxisType === 'date' ? (label: unknown) => (typeof label === 'number' ? formatDateAxisValue(label) : (label as React.ReactNode)) : undefined;
  // Recharts defaults a numeric axis's domain to `[0, 'auto']`, not the data's own range — for
  // ordinary numbers that just wastes a little axis space, but for epoch-ms dates it drags the
  // domain down to include 1970 (rendering as a stray "12/31/69" tick) and crushes the real data
  // into a sliver at the far end. Forcing the domain to the actual data bounds fixes both.
  const xDomain = numericX ? (['dataMin', 'dataMax'] as const) : undefined;
  // Grey, dashed, unobtrusive — a reference for "where did the teacher put the threshold", not a
  // data series. Independently enabled per axis, so both can render at once. The x line only ever
  // renders when the x-axis is numeric (gated in the editor: a category axis has no continuous
  // value to draw a vertical reference against).
  const thresholds = data.sonificationThresholds;
  const thresholdLabelStyle = { fill: 'var(--color-ws-muted)', fontSize: 11 };
  const yThresholdLine = thresholds?.y !== undefined ? (
    <ReferenceLine
      y={thresholds.y}
      stroke='var(--color-ws-muted)'
      strokeDasharray='4 4'
      label={{ value: String(thresholds.y), position: 'insideTopRight', style: thresholdLabelStyle }}
    />
  ) : null;
  const xThresholdLine = thresholds?.x !== undefined ? (
    <ReferenceLine
      x={thresholds.x}
      stroke='var(--color-ws-muted)'
      strokeDasharray='4 4'
      label={{ value: data.xAxisType === 'date' ? formatDateAxisValue(thresholds.x) : String(thresholds.x), position: 'insideTopRight', style: thresholdLabelStyle }}
    />
  ) : null;
  const highlightDot = highlightedPoint ? (
    <ReferenceDot
      x={highlightedPoint.x}
      y={highlightedPoint.y}
      r={7}
      fill='var(--color-ws-primary)'
      stroke='var(--color-ws-surface)'
      strokeWidth={2}
    />
  ) : null;
  // Deliberately undashed and thin, distinct from the grey dashed threshold lines — a moving
  // playhead, not a fixed reference the teacher configured.
  const playhead = playheadX != null ? (
    <ReferenceLine x={playheadX} stroke='var(--color-ws-primary)' strokeWidth={1.5} strokeOpacity={0.6} />
  ) : null;

  return (
    <div className='flex flex-col gap-1'>
      {/* `aria-hidden`: the chart's own orientation clause opens with "Graph title: …, type: …",
          and the chart region is named with the same title, so this heard a third time is noise. */}
      {data.title && <p aria-hidden='true' className='text-center text-sm font-medium text-ws-text'>{data.title}</p>}
      {data.chartType === 'scatter' ? (
        <>
          <ResponsiveContainer width='100%' height={height}>
            <ScatterChart margin={margin} title={chartAccessibleTitle}>
              <CartesianGrid stroke={GRID_STROKE} strokeDasharray='3 3' />
              <XAxis dataKey='x' type={numericX ? 'number' : 'category'} domain={xDomain} name={data.xAxisLabel} stroke={AXIS_STROKE} tick={TICK_STYLE} label={xAxisLabelProps} tickFormatter={xTickFormatter} />
              <YAxis dataKey='y' type='number' domain={yDomain} ticks={yTicks} name={data.yAxisLabel} width={yAxisWidth} stroke={AXIS_STROKE} tick={TICK_STYLE} label={yAxisLabelProps} />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                contentStyle={TOOLTIP_CONTENT_STYLE}
                formatter={(value, name) => (data.xAxisType === 'date' && (name === data.xAxisLabel || name === 'x') && typeof value === 'number' ? formatDateAxisValue(value) : value)}
                isAnimationActive={false}
              />
              {yThresholdLine}
              {xThresholdLine}
              {data.series.map((s, i) => {
                const shape = defaultSeriesShape(i);
                const dimmed = isDimmed(s.id);
                return (
                  <Scatter
                    key={s.id}
                    name={seriesName(s, i)}
                    data={s.points}
                    fill={seriesColor(i, s)}
                    shape={(props: { cx?: number; cy?: number }) => (
                      <SeriesMarker cx={props.cx} cy={props.cy} shape={shape} size={6} fill={seriesColor(i, s)} dimmed={dimmed} />
                    )}
                  />
                );
              })}
              {highlightDot}
              {playhead}
            </ScatterChart>
          </ResponsiveContainer>
          {showLegend && (
            <DataVizLegend data={data} hoveredSeriesId={hoveredSeriesId} onHoverSeries={setHoveredSeriesId} armedSeriesId={armedSeriesId} seriesColor={seriesColor} seriesName={seriesName} />
          )}
        </>
      ) : (
        (() => {
          const rows = toRechartsRows(data.series);
          const ChartComponent = data.chartType === 'bar' ? BarChart : LineChart;
          return (
            <>
              <ResponsiveContainer width='100%' height={height}>
                <ChartComponent data={rows} margin={margin} title={chartAccessibleTitle}>
                  <CartesianGrid stroke={GRID_STROKE} strokeDasharray='3 3' />
                  <XAxis dataKey='x' type={numericX ? 'number' : 'category'} domain={xDomain} stroke={AXIS_STROKE} tick={TICK_STYLE} label={xAxisLabelProps} tickFormatter={xTickFormatter} />
                  <YAxis domain={yDomain} ticks={yTicks} width={yAxisWidth} stroke={AXIS_STROKE} tick={TICK_STYLE} label={yAxisLabelProps} />
                  <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelFormatter={xTooltipLabelFormatter} isAnimationActive={false} />
                  {yThresholdLine}
                  {xThresholdLine}
                  {data.series.map((s, i) => {
                    const dimmed = isDimmed(s.id);
                    if (data.chartType === 'bar') {
                      return <Bar key={s.id} dataKey={s.id} name={seriesName(s, i)} fill={seriesColor(i, s)} fillOpacity={dimmed ? 0.3 : 1} radius={[3, 3, 0, 0]} />;
                    }
                    const shape = defaultSeriesShape(i);
                    return (
                      // 'linear', not 'monotone' — a smoothed curve between points misrepresents the
                      // data as continuously interpolated when only the plotted points are real.
                      <Line
                        key={s.id}
                        type='linear'
                        dataKey={s.id}
                        name={seriesName(s, i)}
                        stroke={seriesColor(i, s)}
                        strokeWidth={2}
                        strokeOpacity={dimmed ? 0.3 : 1}
                        connectNulls={false}
                        dot={(props: { cx?: number; cy?: number }) => (
                          <SeriesMarker cx={props.cx} cy={props.cy} shape={shape} size={5} fill={seriesColor(i, s)} dimmed={dimmed} />
                        )}
                      />
                    );
                  })}
                  {highlightDot}
                  {playhead}
                </ChartComponent>
              </ResponsiveContainer>
              {showLegend && (
                <DataVizLegend data={data} hoveredSeriesId={hoveredSeriesId} onHoverSeries={setHoveredSeriesId} armedSeriesId={armedSeriesId} seriesColor={seriesColor} seriesName={seriesName} />
              )}
            </>
          );
        })()
      )}
    </div>
  );
});
